import { useEffect, useState } from "react"

/**
 * Returns a debounced copy of a value that only updates after the input has
 * stopped changing for `delayMs`. Each new input resets the timer, so while a
 * value changes rapidly (e.g. a slider dragged continuously) the returned value
 * holds steady and settles once the changes pause.
 *
 * Use this to keep expensive, selection-driven work off the critical path of a
 * fast-changing input without throttling the input itself.
 *
 * @param value - The source value to debounce. Compared by reference, so for
 *   object values pass a stable reference (or a primitive) to avoid the timer
 *   resetting every render.
 * @param delayMs - Milliseconds the value must be stable before it is emitted.
 * @returns The most recent value that has stayed unchanged for `delayMs`.
 */
export function useDebouncedValue<T>({
  value,
  delayMs,
}: {
  value: T
  delayMs: number
}): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const timerId: ReturnType<typeof setTimeout> = setTimeout(() => {
      setDebouncedValue(value)
    }, delayMs)

    // Cancel the pending update if the value changes again before it fires,
    // which is what collapses a burst of changes into a single settled update.
    return () => clearTimeout(timerId)
  }, [value, delayMs])

  return debouncedValue
}
